import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { PageShell } from "./Overview";
import { countriesApi, entitiesApi, paymentsApi, aiApi } from "@/lib/api";
import { useCurrency } from "@/lib/currency";
import { Check, ArrowRight, Sparkles, Wand2 } from "lucide-react";
import { toast } from "sonner";

const steps = ["Jurisdiction", "Entity type", "Details", "Review & pay"];

const RegisterWizard = () => {
  const nav = useNavigate();
  const { currency, format } = useCurrency();
  const [countries, setCountries] = useState([]);
  const [step, setStep] = useState(0);
  const [country, setCountry] = useState(null);
  const [entityType, setEntityType] = useState(null);
  const [details, setDetails] = useState({ company_name: "", directors: 1, shareholders: 1, business_description: "" });
  const [creating, setCreating] = useState(false);

  // AI form-fill state
  const [aiDesc, setAiDesc] = useState("");
  const [aiLoading, setAiLoading] = useState(false);
  const [aiResult, setAiResult] = useState(null);

  useEffect(() => { countriesApi.list().then(setCountries).catch(() => {}); }, []);

  const next = () => setStep((s) => Math.min(s + 1, steps.length - 1));
  const back = () => setStep((s) => Math.max(s - 1, 0));

  const runAi = async () => {
    if (aiDesc.trim().length < 10) {
      toast.error("Describe your business in at least a sentence (10+ chars)");
      return;
    }
    setAiLoading(true);
    setAiResult(null);
    try {
      const r = await aiApi.formFill(aiDesc.trim());
      setAiResult(r);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "AI could not parse that");
    } finally {
      setAiLoading(false);
    }
  };

  const applyAi = () => {
    if (!aiResult) return;
    const c = countries.find((x) => x.code === aiResult.recommended_country);
    const et = c?.entity_types.find((x) => x.id === aiResult.recommended_entity_type);
    if (!c || !et) { toast.error("AI result no longer valid"); return; }
    setCountry(c);
    setEntityType(et);
    setDetails({
      company_name: aiResult.company_name_suggestions?.[0] || "",
      directors: aiResult.directors || 1,
      shareholders: aiResult.shareholders || 1,
      business_description: aiResult.business_description || aiDesc,
    });
    setStep(2);
    toast.success("Pre-filled — tweak anything you'd like");
  };

  const submit = async () => {
    setCreating(true);
    try {
      const entity = await entitiesApi.create({
        country: country.code,
        entity_type: entityType.id,
        company_name: details.company_name,
        directors: Number(details.directors),
        shareholders: Number(details.shareholders),
        business_description: details.business_description,
      });
      const { url } = await paymentsApi.checkout({ entity_id: entity.id, origin_url: window.location.origin, currency });
      window.location.href = url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not start incorporation");
      setCreating(false);
    }
  };

  return (
    <PageShell title="Incorporate" subtitle="New entity" data-testid="register-wizard">
      {/* stepper */}
      <div className="grid grid-cols-4 gap-px bg-border-subtle border border-border-subtle mb-10" data-testid="wizard-stepper">
        {steps.map((s, i) => (
          <div key={s} className={`p-4 ${i === step ? "bg-surface-card" : "bg-surface-app"}`}>
            <div className="text-[10px] font-mono-accent uppercase tracking-[0.28em] text-ink-muted">Step {String(i + 1).padStart(2, "0")}</div>
            <div className={`mt-1 text-sm ${i === step ? "text-artha-amber" : "text-ink-body"}`}>{s}</div>
          </div>
        ))}
      </div>

      {step === 0 && (
        <>
          {/* AI Magic Fill */}
          <div className="border border-border-subtle bg-surface-card p-6 mb-8" data-testid="ai-magic-panel">
            <div className="flex items-center gap-2 mb-1">
              <Sparkles size={14} className="text-artha-amber" />
              <span className="text-xs font-mono-accent uppercase tracking-[0.22em] text-artha-amber">AI magic fill</span>
            </div>
            <h2 className="h-display text-3xl md:text-4xl">Describe your business. <span className="italic">We'll draft the form.</span></h2>
            <p className="text-sm text-ink-muted mt-2 max-w-2xl">
              Claude-grade reasoning recommends a jurisdiction, entity type, and company name — then pre-fills the wizard.
            </p>
            <div className="mt-5 grid grid-cols-1 md:grid-cols-12 gap-3">
              <textarea
                value={aiDesc}
                onChange={(e) => setAiDesc(e.target.value)}
                placeholder="e.g. Two technical co-founders building an AI SaaS for enterprise finance. Planning a seed round from US VCs. Customers in the US and EU."
                rows={3}
                data-testid="ai-magic-description"
                className="md:col-span-9 bg-transparent border border-border-subtle p-3 text-sm focus:outline-none focus:border-artha-amber"
              />
              <button onClick={runAi} disabled={aiLoading}
                data-testid="ai-magic-submit"
                className="md:col-span-3 bg-artha-amber text-artha-ink py-3 text-sm tracking-tight hover:bg-artha-cream transition-colors disabled:opacity-60 inline-flex items-center justify-center gap-2">
                <Wand2 size={14} /> {aiLoading ? "Thinking…" : "Draft for me"}
              </button>
            </div>

            {aiResult && (
              <div className="mt-6 border border-artha-amber/40 bg-artha-amber/5 p-5" data-testid="ai-magic-result">
                <div className="text-[10px] font-mono-accent uppercase tracking-[0.22em] text-artha-amber mb-3">
                  Recommendation
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                  <MiniAi label="Jurisdiction" value={aiResult.recommended_country_name} />
                  <MiniAi label="Entity type" value={aiResult.recommended_entity_type_name} />
                  <MiniAi label="Dir / SH" value={`${aiResult.directors} / ${aiResult.shareholders}`} />
                </div>
                {aiResult.company_name_suggestions?.length > 0 && (
                  <div className="mb-4">
                    <div className="text-[10px] font-mono-accent uppercase tracking-[0.22em] text-ink-muted mb-2">
                      Name suggestions
                    </div>
                    <div className="flex gap-2 flex-wrap">
                      {aiResult.company_name_suggestions.map((n, i) => (
                        <span key={i} className="text-xs font-mono-accent border border-border-subtle px-2 py-1">{n}</span>
                      ))}
                    </div>
                  </div>
                )}
                {aiResult.reasoning && (
                  <div className="text-sm text-ink-muted leading-relaxed italic mb-4">
                    "{aiResult.reasoning}"
                  </div>
                )}
                <button onClick={applyAi} data-testid="ai-magic-apply"
                  className="bg-artha-amber text-artha-ink px-5 py-2.5 text-sm hover:bg-artha-cream transition-colors inline-flex items-center gap-2">
                  <Check size={14} /> Apply to wizard
                </button>
              </div>
            )}
          </div>

          <div className="text-xs font-mono-accent uppercase tracking-[0.22em] text-ink-muted mb-3">Or pick manually</div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-border-subtle border border-border-subtle" data-testid="step-country">
            {countries.map((c) => (
              <button key={c.code} onClick={() => { setCountry(c); setEntityType(null); next(); }}
                data-testid={`wizard-country-${c.code}`}
                className={`bg-surface-card p-8 text-left hover:bg-surface-app transition-colors ${country?.code === c.code ? "outline outline-1 outline-artha-amber" : ""}`}>
                <div className="text-4xl mb-6">{c.flag}</div>
                <div className="h-display text-2xl">{c.name}</div>
                <div className="text-xs text-ink-muted mt-2 font-mono-accent uppercase tracking-[0.18em]">{c.entity_types.length} entity types</div>
                <div className="text-sm text-ink-muted mt-4">{c.tagline}</div>
              </button>
            ))}
          </div>
        </>
      )}

      {step === 1 && country && (
        <div data-testid="step-entity-type">
          <div className="text-xs font-mono-accent uppercase tracking-[0.28em] text-ink-muted mb-4">{country.flag} {country.name}</div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-border-subtle border border-border-subtle">
            {country.entity_types.map((et) => (
              <button key={et.id} onClick={() => { setEntityType(et); next(); }}
                data-testid={`wizard-entity-type-${et.id}`}
                className={`bg-surface-card p-8 text-left hover:bg-surface-app transition-colors flex flex-col ${entityType?.id === et.id ? "outline outline-1 outline-artha-amber" : ""}`}>
                <div className="h-display text-2xl">{et.name}</div>
                <p className="text-sm text-ink-muted mt-3 leading-relaxed flex-1">{et.description}</p>
                <div className="mt-6 flex items-end justify-between">
                  <div>
                    <div className="font-mono-accent text-2xl text-artha-amber">{format(et.price)}</div>
                    <div className="text-[10px] uppercase tracking-[0.2em] font-mono-accent text-ink-muted">~{et.timeline_days} days</div>
                  </div>
                  <ArrowRight size={18} className="text-ink-muted" />
                </div>
              </button>
            ))}
          </div>
          <NavRow onBack={back} onNext={next} nextDisabled={!entityType} />
        </div>
      )}

      {step === 2 && entityType && (
        <div className="max-w-2xl" data-testid="step-details">
          <div className="text-xs font-mono-accent uppercase tracking-[0.28em] text-ink-muted mb-4">
            {country.flag} {country.name} · {entityType.name}
          </div>
          <div className="space-y-5">
            <DField label="Proposed company name" id="company_name" value={details.company_name}
              onChange={(v) => setDetails({ ...details, company_name: v })}
              placeholder="e.g., Acme Labs" />
            <div className="grid grid-cols-2 gap-4">
              <DField label={`Directors (min ${entityType.min_directors})`} id="directors" type="number"
                value={details.directors} onChange={(v) => setDetails({ ...details, directors: v })} />
              <DField label={`Shareholders (min ${entityType.min_shareholders})`} id="shareholders" type="number"
                value={details.shareholders} onChange={(v) => setDetails({ ...details, shareholders: v })} />
            </div>
            <div>
              <label className="block text-xs uppercase tracking-[0.2em] text-ink-muted font-mono-accent mb-2">
                Business description
              </label>
              <textarea data-testid="input-business-description" value={details.business_description}
                onChange={(e) => setDetails({ ...details, business_description: e.target.value })}
                rows={4}
                className="w-full bg-transparent border border-border-subtle px-4 py-3 text-sm focus:outline-none focus:border-artha-amber transition-colors" />
            </div>
          </div>
          <NavRow onBack={back} onNext={next} nextDisabled={!details.company_name} />
        </div>
      )}

      {step === 3 && (
        <div className="max-w-2xl" data-testid="step-review">
          <h2 className="h-display text-3xl mb-6">Review & pay</h2>
          <div className="border border-border-subtle divide-y divide-border-subtle">
            <Row k="Jurisdiction" v={`${country.flag} ${country.name}`} />
            <Row k="Entity" v={entityType.name} />
            <Row k="Company name" v={details.company_name} />
            <Row k="Directors" v={details.directors} />
            <Row k="Shareholders" v={details.shareholders} />
            <Row k="Incorporation fee" v={<span className="text-artha-amber font-mono-accent">{format(entityType.price)} {currency}</span>} />
          </div>
          <p className="text-xs text-ink-muted mt-4 leading-relaxed">
            Payment is processed via Stripe. A verified local partner will be assigned on successful payment and begin the filing within {entityType.timeline_days} business days.
          </p>
          <div className="flex gap-3 mt-8">
            <button onClick={back} data-testid="wizard-back-button"
              className="px-6 py-3 text-sm border border-border-subtle hover:border-ink-body">Back</button>
            <button onClick={submit} disabled={creating} data-testid="wizard-pay-button"
              className="flex-1 bg-artha-amber text-artha-ink px-6 py-3 text-sm tracking-tight hover:bg-artha-cream transition-colors disabled:opacity-60">
              {creating ? "Preparing checkout…" : `Pay ${format(entityType.price)} & assign partner`}
            </button>
          </div>
        </div>
      )}
    </PageShell>
  );
};

const DField = ({ label, id, type = "text", value, onChange, placeholder }) => (
  <div>
    <label className="block text-xs uppercase tracking-[0.2em] text-ink-muted font-mono-accent mb-2">{label}</label>
    <input id={id} type={type} value={value} placeholder={placeholder} data-testid={`input-${id}`}
      onChange={(e) => onChange(e.target.value)}
      className="w-full bg-transparent border border-border-subtle px-4 py-3.5 text-sm focus:outline-none focus:border-artha-amber transition-colors" />
  </div>
);

const NavRow = ({ onBack, onNext, nextDisabled }) => (
  <div className="flex gap-3 mt-8">
    <button onClick={onBack} data-testid="wizard-back-button"
      className="px-6 py-3 text-sm border border-border-subtle hover:border-ink-body">Back</button>
    <button onClick={onNext} disabled={nextDisabled} data-testid="wizard-next-button"
      className="bg-artha-amber text-artha-ink px-6 py-3 text-sm tracking-tight hover:bg-artha-cream transition-colors disabled:opacity-50">
      Continue
    </button>
  </div>
);

const Row = ({ k, v }) => (
  <div className="flex justify-between items-center px-5 py-4">
    <span className="text-xs uppercase tracking-[0.2em] font-mono-accent text-ink-muted">{k}</span>
    <span className="text-sm text-ink-body">{v}</span>
  </div>
);

const MiniAi = ({ label, value }) => (
  <div className="border border-border-subtle bg-surface-app px-4 py-3">
    <div className="text-[10px] font-mono-accent uppercase tracking-[0.22em] text-ink-muted">{label}</div>
    <div className="text-sm mt-1">{value}</div>
  </div>
);

export default RegisterWizard;
